import { T } from '../theme'
import {
  KIT_COLORS,
  couleursEnvoyables,
  estCouleurValide,
  libelleCouleur,
} from '../utils/kitColors'

/**
 * KitColorConfirm — les deux maillots du match, cote a cote.
 *
 * POURQUOI CE BLOC EXISTE, ET POURQUOI IL EST FORMULE EN QUESTION.
 *
 * Ces deux couleurs partent au prestataire, qui les RENVOIE telles quelles pour
 * dire quelle equipe est la notre. Il ne verifie rien. Une couleur perimee
 * — maillot de coupe, jeu impose par l'arbitre, nouveau sponsor — produit un
 * rapport entierement inverse : le coach lit les statistiques de l'adversaire
 * en croyant lire les siennes, et le rapport, le PDF et l'Insightboard
 * repeignent tout a l'envers, l'equipe du coach etant peinte en bleu et
 * l'adversaire en or. Rien, nulle part, ne le detecte.
 *
 * C'est le seul endroit du produit ou une erreur de saisie devient un rapport
 * faux et silencieux. D'ou trois partis pris ·
 *
 *   1. UNE CONFIRMATION, PAS UN FORMULAIRE. « Tu jouais en noir ? » se relit,
 *      la ou un champ prerempli se traverse sans le voir.
 *   2. LES DEUX COULEURS VISIBLES EN MEME TEMPS, jamais l'une derriere un lien
 *      « modifier le defaut ». Deux pastilles cote a cote rendent un conflit
 *      evident avant meme le message d'erreur.
 *   3. LA COULEUR DE L'ADVERSAIRE N'A AUCUN DEFAUT POSSIBLE, donc elle est
 *      toujours a saisir. C'est elle qui force l'attention sur le bloc entier.
 *
 * Le bloc n'impose rien : sans les deux couleurs, l'analyse se fait comme avant
 * ce lot, simplement sans ancrage. On ne bloque pas un coach pressé, on lui
 * montre ce qu'il perd.
 */

const SANS_COULEUR = ''

/**
 * Deux palettes, une seule structure. La modale timeline de MatchDetail est sur
 * fond sombre · y poser un bloc clair le ferait lire comme un element etranger
 * colle apres coup, alors que c'est le bloc le plus important de l'ecran.
 */
const PALETTES = {
  clair: {
    fond: T.surface, encre: T.ink, attenue: T.muted,
    filet: T.rule, champFond: T.surface,
  },
  sombre: {
    fond: 'rgba(255,255,255,0.03)', encre: '#f5f2eb', attenue: 'rgba(245,242,235,0.45)',
    filet: 'rgba(255,255,255,0.10)', champFond: 'rgba(0,0,0,0.25)',
  },
}

function Pastille({ valeur, taille = 16, p = PALETTES.clair }) {
  const c = KIT_COLORS.find(k => k.valeur === valeur)
  return (
    <span
      aria-hidden="true"
      style={{
        width: taille, height: taille, borderRadius: '50%', flexShrink: 0,
        display: 'inline-block',
        background: c?.pastille || 'transparent',
        border: `1px solid ${c?.bordure || (c ? 'rgba(127,127,127,0.35)' : p.filet)}`,
      }}
    />
  )
}

// UN SEUL LIBELLE POUR L'ETAT VIDE — PARCOURS-VIDEO-DECLARE-002.
//
// Les deux champs affichaient « Non renseigné » et « À choisir… » pour
// EXACTEMENT le meme etat. Deux mots pour une meme chose laissent croire a deux
// etats differents · l'un facultatif, l'autre requis. Les deux sont requis.
//
// La valeur reste la chaine vide · aucune couleur ne peut donc etre soumise par
// defaut, y compris quand le club n'a rien renseigne.
const LIBELLE_VIDE = 'À choisir…'

/**
 * Les deux couleurs sont-elles utilisables ? Memes trois conditions que le
 * backend · presentes ENSEMBLE, valides, et DIFFERENTES.
 *
 * Exportee pour que les ecrans de creation posent la MEME regle que le bloc ·
 * une garde recopiee a la main dans chaque page finit par diverger de celle
 * qu'elle est censee refleter.
 */
export function couleursCompletes(nous, adversaire) {
  return couleursEnvoyables(nous, adversaire)
}

function Champ({ id, question, valeur, onChange, enConflit, p }) {
  return (
    <div style={{ flex: 1, minWidth: 190 }}>
      <label
        htmlFor={id}
        style={{
          display: 'block', fontFamily: T.mono, fontSize: 10, letterSpacing: '.06em',
          color: p.encre, marginBottom: 7, lineHeight: 1.5,
        }}
      >
        {question}
      </label>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 9 }}>
        <Pastille valeur={valeur} taille={18} p={p} />
        <select
          id={id}
          value={valeur || SANS_COULEUR}
          onChange={e => onChange(e.target.value)}
          style={{
            flex: 1, minWidth: 0, padding: '9px 10px',
            fontFamily: T.mono, fontSize: 11, color: p.encre,
            background: p.champFond,
            border: `1px solid ${enConflit ? T.red : p.filet}`,
            cursor: 'pointer',
          }}
        >
          <option value={SANS_COULEUR}>
            {LIBELLE_VIDE}
          </option>
          {KIT_COLORS.map(c => (
            <option key={c.valeur} value={c.valeur}>{c.label}</option>
          ))}
        </select>
      </div>
    </div>
  )
}

/**
 * @param {string} couleurNous - valeur courante, '' si non renseignee
 * @param {string} couleurAdverse
 * @param {(v:string)=>void} onChangeNous
 * @param {(v:string)=>void} onChangeAdverse
 * @param {boolean} compact - marges reduites (modale)
 */
export default function KitColorConfirm({
  couleurNous,
  couleurAdverse,
  onChangeNous,
  onChangeAdverse,
  compact = false,
  sombre = false,
}) {
  const p = sombre ? PALETTES.sombre : PALETTES.clair
  const conflit = (
    estCouleurValide(couleurNous) &&
    estCouleurValide(couleurAdverse) &&
    couleurNous === couleurAdverse
  )
  const complet = couleursEnvoyables(couleurNous, couleurAdverse)
  // Ni complet ni en conflit : il manque simplement une couleur. Ce n'est pas
  // une erreur — c'est l'etat de tous les matchs d'avant ce lot.
  const incomplet = !complet && !conflit

  return (
    <div
      data-testid="kit-colors"
      style={{
        border: `1px solid ${conflit ? T.red : p.filet}`,
        borderLeft: `2px solid ${conflit ? T.red : T.gold}`,
        background: p.fond,
        padding: compact ? '14px 16px' : '18px 20px',
        marginTop: compact ? 14 : 18,
      }}
    >
      <div style={{
        fontFamily: T.mono, fontSize: 9, letterSpacing: '.16em',
        textTransform: 'uppercase', color: T.gold, marginBottom: 4,
      }}>
        Les maillots de ce match
      </div>
      <p style={{
        fontFamily: T.mono, fontSize: 10, color: p.attenue,
        lineHeight: 1.65, margin: '0 0 14px',
      }}>
        C'est ce qui permet de reconnaître ton équipe dans la vidéo. Vérifie les
        deux couleurs : elles servent à te séparer de l'adversaire dans tout le
        rapport.
      </p>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <Champ
          id="kit-nous"
          question="Tu jouais en quelle couleur ?"
          valeur={couleurNous}
          onChange={onChangeNous}
          enConflit={conflit}
          p={p}
        />
        <Champ
          id="kit-adverse"
          question="Et l'adversaire ?"
          valeur={couleurAdverse}
          onChange={onChangeAdverse}
          enConflit={conflit}
          p={p}
        />
      </div>

      {conflit && (
        <p role="alert" style={{
          fontFamily: T.mono, fontSize: 10, color: T.red,
          lineHeight: 1.6, margin: '12px 0 0',
        }}>
          Les deux équipes ne peuvent pas porter du {libelleCouleur(couleurNous).toLowerCase()} :
          rien ne les distinguerait dans la vidéo. Choisis la couleur réellement portée ce jour-là.
        </p>
      )}

      {incomplet && (
        <p role="alert" style={{
          fontFamily: T.mono, fontSize: 10, color: T.red,
          lineHeight: 1.6, margin: '12px 0 0',
        }}>
          {/* LES DEUX COULEURS SONT DESORMAIS OBLIGATOIRES · arbitrage produit
              du 11/08. La version precedente disait « l'analyse se fait quand
              même » · c'etait vrai, et c'est precisement le probleme · elle se
              faisait sans savoir quelle equipe etait celle du coach. */}
          Choisis les deux couleurs pour continuer. Sans elles, le rapport ne
          peut pas te séparer de l'adversaire.
        </p>
      )}

      {complet && (
        <p style={{
          fontFamily: T.mono, fontSize: 10, color: p.attenue,
          lineHeight: 1.6, margin: '12px 0 0',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        }}>
          <Pastille valeur={couleurNous} p={p} />
          <span>ton équipe en {libelleCouleur(couleurNous).toLowerCase()},</span>
          <Pastille valeur={couleurAdverse} p={p} />
          <span>l'adversaire en {libelleCouleur(couleurAdverse).toLowerCase()}.</span>
        </p>
      )}
    </div>
  )
}
